import Constants from "@/utils/constants";
import DBOperationsComb from "@/utils/dbOperationsComb";
import GpsTrackerDBOperations from "@/utils/gpsTrackerDBOperations";
import Exception from "@/utils/exception";

async function getPatrolFromFleetComplete(date, orderBy = ["timestamp"]) {
  try {
    const { data, error } =
      await GpsTrackerDBOperations.getFleetCompleteGpsByDate(date, orderBy);

    if (error) throw error;

    return {
      error: null,
      data: data || []
    };
  } catch (error) {
    return {
      error: error.message || "Failed to fetch patrol reports",
      data: []
    };
  }
}

async function getPatrolFromRoadWorks(
  date,
  orderBy = ["event_elapsedRealtimeNanos"]
) {
  try {
    const batchSize = 1000;
    let allData = [];
    let hasMore = true;
    let startIndex = 0;

    while (hasMore) {
      // Convert local date to UTC
      let startTime = new Date(date);
      let endTime = new Date(date);
      endTime.setHours(startTime.getHours() + 24);

      startTime = startTime.toISOString();
      endTime = endTime.toISOString();

      const { data, error } =
        await GpsTrackerDBOperations.getCma519UserLocations(
          startTime,
          endTime,
          startIndex,
          startIndex + batchSize - 1,
          orderBy
        );

      if (error) throw error;

      if (data && data.length > 0) {
        // formate the data
        const formattedData = data.map((item) => {
          return {
            Asset: `${item.userDisplayName}, ${item.deviceId}`,
            Date: item.timestamp.split("T")[0],
            Time: new Date(item.timestamp).toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit"
            }),
            Latitude: item.latitude,
            Longitude: item.longitude
          };
        });

        allData = [...allData, ...formattedData];
        startIndex += batchSize;
      } else {
        hasMore = false;
      }
    }

    return {
      error: null,
      data: allData
    };
  } catch (error) {
    console.log("Error:", error);
    return {
      error: error.message || "Failed to fetch patrol reports",
      data: []
    };
  }
}

async function getPatrolReportList(date, orderBy = ["timestamp"]) {
  try {
    const _res1 = await getPatrolFromFleetComplete(date, orderBy);
    console.log("Fleet complete data:", _res1.data.length);
    const _res2 = await getPatrolFromRoadWorks(date);
    console.log("Road works data:", _res2.data.length);
    return {
      error: null,
      data: [..._res1.data, ..._res2.data]
    };
  } catch (error) {
    Exception.catchException(error);
    throw error;
  }
}

async function getPatrolStatusReport(date, areaId = null, regionId = null) {
  try {
    const _res = await GpsTrackerDBOperations.getPatrolStatusReport(
      date,
      areaId,
      regionId
    );
    return _res;
  } catch (error) {
    Exception.catchException(error);
    throw error;
  }
}

let gpsWindowCallsInFlight = 0;
const gpsWindowCallQueue = [];

async function runGpsWindowCall(call) {
  if (
    gpsWindowCallsInFlight >=
    Constants.constants.GPS_ROUTE_MAX_CONCURRENT_WINDOWS
  ) {
    await new Promise((resolve) => gpsWindowCallQueue.push(resolve));
  } else {
    gpsWindowCallsInFlight++;
  }
  try {
    return await call();
  } finally {
    const next = gpsWindowCallQueue.shift();
    if (next) next();
    else gpsWindowCallsInFlight--;
  }
}

/**
 * The whole Edmonton day for one device, fetched as fixed
 * GPS_ROUTE_WINDOW_HOURS windows in parallel (PHO-1258) and joined in order.
 * Any window failing returns its error with no data, never a partial track.
 * Highway / truck filtering still applies when highwayId / truckId are passed.
 */
async function getPatrolStatusReportGPSData(
  highwayId,
  date,
  deviceId = null,
  truckId = null
) {
  try {
    const windowHours = Constants.constants.GPS_ROUTE_WINDOW_HOURS;
    const windows = [];
    for (let fromHour = 0; fromHour < 24; fromHour += windowHours) {
      windows.push({ fromHour, toHour: fromHour + windowHours });
    }

    const results = await Promise.all(
      windows.map(({ fromHour, toHour }) =>
        runGpsWindowCall(() =>
          GpsTrackerDBOperations.getPatrolReportGpsData({
            highwayId,
            date,
            deviceId,
            truckId,
            fromHour,
            toHour
          })
        )
      )
    );

    const failed = results.find((res) => res.error);
    if (failed) return { data: null, error: failed.error };

    return { data: results.flatMap((res) => res.data ?? []), error: null };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getPartialPatrolData(weekStartDate) {
  try {
    const weekEndDate = new Date(weekStartDate);
    // setting up end date to Saturday
    weekEndDate.setDate(weekEndDate.getDate() + 6);
    const _res = await GpsTrackerDBOperations.getPatrolStatusForWeek(
      weekStartDate.toISOString(),
      weekEndDate.toISOString()
    );

    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

/** Fetch patrol status records for a single highway and date (any status), e.g. for patrolled view */
async function getPartialPatrolDataByHighwayAndDate(hwyUuid, date) {
  try {
    const d = new Date(date);
    const startOfDay = new Date(d);
    startOfDay.setUTCHours(0, 0, 0, 0);
    const endOfDay = new Date(d);
    endOfDay.setUTCHours(23, 59, 59, 999);
    const _res = await GpsTrackerDBOperations.getPatrolStatusForHighwayAndDay(
      hwyUuid,
      startOfDay.toISOString(),
      endOfDay.toISOString()
    );

    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getWeeklyPatrolStatusReportQCData(config = {}) {
  try {
    const _res = await DBOperationsComb.fetchData(config, false);
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function upsertWeeklyPatrolStatusReportQCData(data) {
  try {
    if (data.id) {
      return await DBOperationsComb.updateData(
        Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_REPORT_QC_DATA_TABLE,
        data,
        false
      );
    } else {
      return await DBOperationsComb.insertData(
        Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_REPORT_QC_DATA_TABLE,
        data,
        false
      );
    }
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getWeeklyPatrolStatusReportQCHistory(config = {}) {
  try {
    const _res = await DBOperationsComb.fetchData(config, false);
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getWeeklyDefectsReport(
  weekStartDate,
  areaIds = null,
  regionId = null
) {
  try {
    const statuses = Constants.enums.DEFECTS_STATUS;
    const statusesArray = [
      statuses.NEW,
      statuses.SUBMITTED,
      statuses.PENDING,
      statuses.ON_HOLD,
      statuses.IN_PROGRESS,
      statuses.DONE,
      statuses.QC_FAIL,
      statuses.QC_CHECK,
      statuses.IN_REVIEW,
      statuses.COMPLETED
    ];
    const priority1Defects = await DBOperationsComb.getDefectsReport({
      startDate: weekStartDate,
      statuses: statusesArray,
      priority: "1",
      areaIds,
      regionId
    });
    const allDefects = await DBOperationsComb.getDefectsReport({
      startDate: weekStartDate,
      statuses: statusesArray,
      priority: "(^|[^0-9])(1|2)([^0-9]|$)", //checks for 1 or 2 in the string for priority
      areaIds,
      regionId
    });
    return {
      priority1Defects: priority1Defects.data,
      allDefects: allDefects.data
    };
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

/** Patrol-app lifecycle events (PATROL START / STOP / etc.) for one device, one date. */
async function getPatrolEvents(deviceId, date) {
  try {
    const _res = await GpsTrackerDBOperations.getPatrolEvents(deviceId, date);
    return _res;
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

export default {
  getPatrolReportList,
  getPatrolFromRoadWorks,
  getPatrolStatusReport,
  getWeeklyPatrolStatusReportQCData,
  upsertWeeklyPatrolStatusReportQCData,
  getWeeklyPatrolStatusReportQCHistory,
  getPatrolStatusReportGPSData,
  getPatrolEvents,
  getPartialPatrolData,
  getPartialPatrolDataByHighwayAndDate,
  getWeeklyDefectsReport
};
